import { useCallback, useMemo } from 'react';

import { AddPartNoteButton, IPartNoteForm } from 'bom/components';
import { BomPartViewByType, ComponentStatus } from 'bom/enums';
import { useElementActions } from 'bom/hooks';
import { IBomPart, IElement, IElementComponentNote } from 'bom/types';
import { useAgGridClientTableContext } from 'core/states';
import { IJobPartNoteForm, JobPartNoteType } from 'jobs/components/JobDetails';
import { useJobPartNoteActions } from 'jobs/hooks';
import { IJobPart } from 'jobs/types';
import { Variant } from 'theme/enums';

interface IProps {
  jobId: number;
  element?: IElement;
}

export const AddNotesButton = ({ jobId, element }: IProps) => {
  const { selectedRows, updateRows } = useAgGridClientTableContext();

  const { addJobPartNotes } = useJobPartNoteActions();
  const { upsertElementComponentNotes } = useElementActions();

  const selectedActiveJobParts = useMemo(() => {
    return selectedRows.filter(
      (jobPart: IJobPart) => jobPart.status !== ComponentStatus.DELETED
    ) as IJobPart[];
  }, [selectedRows]);

  const isDisabled = useMemo(
    () =>
      selectedActiveJobParts.length === 0 ||
      selectedRows.some((jobPart: IJobPart) => jobPart.status === ComponentStatus.DELETED),
    [selectedActiveJobParts, selectedRows]
  );
  const availableNoteTypes = useMemo(() => {
    if (element) {
      return [JobPartNoteType.DEFAULT, JobPartNoteType.ELEMENT];
    }

    return [JobPartNoteType.DEFAULT];
  }, [element]);

  const onJobPartSubmitSuccess = useCallback(
    ({ bomParts }: { bomParts?: IBomPart[] }) => {
      updateRows({ update: bomParts });
    },
    [updateRows]
  );

  const onElementComponentNotesSubmitSuccess = useCallback(
    ({ elementComponentNotes }: { elementComponentNotes?: IElementComponentNote[] }) => {
      if (!element?.id) {
        return;
      }

      const updatedRows = selectedRows.map((row: IJobPart) => ({
        ...row,
        elementComponentNotesMap: {
          ...row.elementComponentNotesMap,
          [element.id]: [
            ...(row.elementComponentNotesMap?.[element.id] || []),
            ...elementComponentNotes.map((note) => ({
              id: note.id,
              line: note.line,
              content: note.content,
            })),
          ],
        },
      }));

      updateRows({ update: updatedRows });
    },
    [element?.id, selectedRows, updateRows]
  );

  const onSubmit = useCallback(
    () =>
      async (formData: IPartNoteForm): Promise<{ success: boolean }> => {
        const data = formData as unknown as IJobPartNoteForm;

        let success = true;

        const jobPartNote = data.notes[JobPartNoteType.DEFAULT];
        const elementNote = data.notes[JobPartNoteType.ELEMENT];

        if (jobPartNote.enabled) {
          const { success: upsertJobPartNotesSuccess, bomParts } = await addJobPartNotes({
            bomId: jobId,
            viewBy: {
              group: BomPartViewByType.PART,
            },
            componentNote: {
              content: jobPartNote.content,
            },
            ids: selectedRows.map((row) => row.componentId),
          });

          if (!upsertJobPartNotesSuccess) {
            success = false;
          }

          if (bomParts) {
            onJobPartSubmitSuccess({ bomParts });
          }
        }

        if (element?.id && elementNote.enabled) {
          const { success: upsertElementComponentNotesSuccess, elementComponentNotes } =
            await upsertElementComponentNotes({
              elementComponentNotes: selectedRows.map((row: IJobPart) => ({
                elementComponentId: row.elementComponentsMap[element.id].id,
                content: elementNote.content,
              })),
            });

          if (!upsertElementComponentNotesSuccess) {
            success = false;
          }

          if (elementComponentNotes) {
            onElementComponentNotesSubmitSuccess({ elementComponentNotes });
          }
        }

        return { success };
      },
    [
      element?.id,
      jobId,
      selectedRows,
      onJobPartSubmitSuccess,
      addJobPartNotes,
      upsertElementComponentNotes,
      onElementComponentNotesSubmitSuccess,
    ]
  );

  return (
    <AddPartNoteButton
      onSubmit={onSubmit}
      availableNoteTypes={availableNoteTypes}
      buttonProps={{ variant: Variant.OUTLINED, disabled: isDisabled }}
    />
  );
};
